import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import {
  Component,
  effect,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { MessageService } from 'primeng/api';
import { Subscription, forkJoin } from 'rxjs';
import { ApiConstants } from 'src/app/base/api-constants';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { UtilService } from 'src/app/services/util.service';
import { ReferenceUtilService } from '../reference-tree-view/reference-util.service';
import { ModelType, ActionType, UiActions } from '../reference-tree-view/reference.enum';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { TreeTable } from 'primeng/treetable';
import { nodeType } from 'src/app/pages/designs/data-model/data-model.component';
const createColumn = (field: string, header: string, infoBubble = '') => ({ field, header, infoBubble });
import { GlobalConstants } from 'src/app/base/global.constants';
import { cloneDeep } from 'lodash-es';
@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-search-field-settings',
  templateUrl: './search-field-settings.component.html',
  styleUrls: ['./search-field-settings.component.scss'],
  standalone: false  //NOSONAR
})
export class SearchFieldSettingsComponent implements OnInit, OnDestroy, OnChanges {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private bs = inject(HttpBaseService);
  private appGlobalService = inject(AppGlobalService);
  private util = inject(UtilService);
  private referenceUtil = inject(ReferenceUtilService);
  private loader = inject(AppLoaderService);
  private messageService = inject(MessageService);

  @Input() tableIdMap: any = null;
  @Input() disableField = false;
  @Input() formData: any = null;
  @Input() dataModelOrders: any = null;
  @Input() fieldIdMap: any = null;
  @Input() fsData: any;
  tableOrders: any;
  private modelValue: any;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  subscriptions: Subscription[] = [];
  showSearchModal = false;
  isloading = false;
  tableName: any;
  checkBoxChanged = false;
  disableSave = false;
  allFields: any = [];
  foreignkeyMappings: any = [];
  filteredforeignkeyMappingsTreeNodes: any = [];
  savedData: any = {};
  partialSearchRestricted: any = ['Sid', 'Pid'];
  searchValue = '';
  foreignKeySearch = '';
  @ViewChild('searchTbl') searchTbl!: TreeTable; // Reference to the TreeTable
  activeTabIndex = signal(0);
  formHasError = false;
  disabledFields = ['Created By', 'Created Date', 'Modified By', 'Modified Date', 'Sid'];

  fieldCols = [
    createColumn('name', 'FIELD_NAME'),
    createColumn(
      'searchable',
      'SEARCHABLE',
      "Marking the field as 'Searchable' will sync the data in the Elasticsearch. Only the marked columns will be included in Elasticsearch and available in list views for search functionality. Additionally, it is possible to index fields from foreign key-mapped tables and search using those fields."
    ),
    // createColumn("propagate", "PROPAGATE", "Applicable only for the foreign key table fields that are not linked with foreign key indexes."),
    createColumn(
      'partialSearch',
      'PARTIAL_SEARCH',
      'Enable this option to allow partial or incomplete search queries to return relevant results.'
    )
  ];

  fkCols = [
    createColumn('foreignkey', 'FOREIGN_KEY_MAPPING'),
    createColumn(
      'searchable',
      'SEARCHABLE',
      "Marking the field as 'Searchable' will sync the data in the Elasticsearch. Only the marked columns will be included in Elasticsearch and available in list views for search functionality. Additionally, it is possible to index fields from foreign key-mapped tables and search using those fields."
    ),
    createColumn(
      'partialSearch',
      'PARTIAL_SEARCH',
      'Enable this option to allow partial or incomplete search queries to return relevant results.'
    ),
    createColumn(
      'propagate',
      'PROPAGATE',
      'Applicable only for the foreign key table fields that are not linked with foreign key indexes.'
    ),
    createColumn(
      'fkAliasName',
      'ALIAS_NAME_COMPOSITE_FK',
      'It represents the field name of the referenced table field in the search index.'
    )
  ];

  changeTracker: {
    added: any[];
    updated: any[];
    deleted: string[];
  } = {
    added: [],
    updated: [],
    deleted: []
  };
  searchableFieldsResponse: { allFields?: any[]; searchableFields?: any[] } = {};
  foreignKeyFieldsResponse: { searchableForeignKeyFields?: any; referedTableFieldsMap?: any; foreignKeys?: any[] } = {};
  allFieldsFromResponse: any[] = [];
  searchableFieldsFromResponse: any[] = [];
  searchableForeignKeyFields: any = {};
  referedTableFieldsMap: any = {};
  foreignKeysFromResponse: any[] = [];
  private tableFieldsLoaded: boolean = false;
  private foreignKeyFieldsLoaded: boolean = false;
  generatedAliases = new Set<string>();

  get isBetaAccessPrivilegeConsumable(): boolean {
    return this.appGlobalService.betaAccessPrivilegeForApp;
  }

  ngOnDestroy(): void {
    this.resetData();
  }

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.modelValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  openSearchModal() {
    this.showSearchModal = true;
    this.activeTabIndex.set(0);
    this.tableName = this.formData.name;
    this.checkBoxChanged = false;
    this.resetChangeTracker();
    this.loadTableFields();
  }

  onTabChange(event: any) {
    this.activeTabIndex.set(event);
    this.resetData();

    // Load data based on which tab is selected
    if (event === 0) {
      this.loadTableFields();
    } else if (event === 1) {
      this.loadForeignKeyFields();
    }
  }

  loadTableFields() {
    if (this.tableFieldsLoaded) {
      return; // Already loaded, no need to call API again
    }
    this.isloading = true;
    const tblfields = this.getFieldsUsingTableId(this.formData.sid);
    if (tblfields && tblfields.length > 0) {
      // Call only the table fields API
      this.bs.get(ApiConstants.getAllAndSearchableFields, { tableId: this.formData.sid }).subscribe({
        next: (fieldsResponse) => {
          this.searchableFieldsResponse = fieldsResponse as { allFields?: any[]; searchableFields?: any[] };
          this.extractTableFieldsData();
          this.processTableFields(tblfields);
          this.tableFieldsLoaded = true;
          this.isloading = false;
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.translate.instant('SEARCHABLE_TABLE_FIELDS_LOAD_FAILED')
          });
          this.processTableFields(tblfields);
          this.tableFieldsLoaded = true;
          this.isloading = false;
        }
      });
    } else {
      this.isloading = false;
    }
  }

  loadForeignKeyFields() {
    if (this.foreignKeyFieldsLoaded) {
      return; // Already loaded, no need to call API again
    }
    this.isloading = true;
    const tblfields = this.getFieldsUsingTableId(this.formData.sid);
    if (tblfields && tblfields.length > 0) {
      // Call only the foreign key fields API
      this.bs.get(ApiConstants.getForeignKeyAndReferenceFields, { tableId: this.formData.sid }).subscribe({
        next: (foreignKeyFieldsResponse) => {
          this.foreignKeyFieldsResponse = foreignKeyFieldsResponse as {
            searchableForeignKeyFields?: any;
            referedTableFieldsMap?: any;
            foreignKeys?: any[];
          };
          this.extractForeignKeyFieldsData();
          this.processForeignKeyFields(tblfields);
          this.foreignKeyFieldsLoaded = true;
          this.isloading = false;
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.translate.instant('SEARCHABLE_TABLE_FIELDS_LOAD_FAILED')
          });
          this.processForeignKeyFields(tblfields);
          this.foreignKeyFieldsLoaded = true;
          this.isloading = false;
        }
      });
    } else {
      this.isloading = false;
    }
  }

  // Extract data methods (split from the original extractResponseData)
  private extractTableFieldsData() {
    this.allFieldsFromResponse = this.searchableFieldsResponse?.allFields || [];
    this.searchableFieldsFromResponse = this.searchableFieldsResponse?.searchableFields || [];
  }

  private extractForeignKeyFieldsData() {
    this.searchableForeignKeyFields = this.foreignKeyFieldsResponse?.searchableForeignKeyFields || {};
    this.referedTableFieldsMap = this.foreignKeyFieldsResponse?.referedTableFieldsMap || {};
    this.foreignKeysFromResponse = this.foreignKeyFieldsResponse?.foreignKeys || [];
  }

  private processTableFields(tblfields: any[]) {
    // Set saved data for table fields
    this.savedData = {
      fields: this.searchableFieldsFromResponse || [],
      foreignKeyFields: this.savedData?.foreignKeyFields || {} // Preserve existing FK data
    };

    // Transform table fields to tree
    this.allFields = this.transformToTree(tblfields);
  }

  // Process foreign key fields (extracted from original getFields method)
  private processForeignKeyFields(tblfields: any[]) {
    // Set saved data for foreign key fields
    this.savedData = {
      fields: this.savedData?.fields || [], // Preserve existing table fields data
      foreignKeyFields: this.searchableForeignKeyFields || {}
    };

    const foreignKeys = this.getForeignKeyMappings();
    this.generatedAliases.clear();
    this.foreignkeyMappings = [];

    // Build the foreign key tree (existing logic)
    foreignKeys.forEach((fk) => {
      const tableId = fk.parentTableSid;
      const parentTableFields = this.getRefferedFieldsUsingTableId(tableId);
      const lastMapping = fk.mappings[fk.mappings.length - 1];
      const node = {
        data: {
          name: `${fk.fkName} + (${this.tableIdMap[fk.parentTableSid]})`,
          id: fk.fkMapId,
          tableId: fk.parentTableSid,
          fkMapId: fk.fkMapId,
          fkIndexId: fk.indexRefAppId,
          fkLastField: lastMapping?.child,
          showChild: true,
          isSelfReferred: false
        },
        children:
          parentTableFields
            .map((fld) => {
              const field = fld;
              const savedFkField = this.findSavedForeignKeyField(fk.fkMapId, field?.sid);
              const savedField = savedFkField;
              const lastMappingField = tblfields.find((f) => f.data.sid === lastMapping?.child);
              const aliasName = this.generateFKAliasName(lastMappingField?.data?.f_fieldId, field.f_fieldId);

              const formattedFkData = {
                name: field.name,
                id: field.sid,
                searchable: savedField?.sid ? true : false,
                isString: this.checkFieldType(field),
                isTransient: field?.f_transientField,
                partialSearch: savedField?.partialSearch
                  ? savedField?.partialSearch
                  : this.checkFieldType(field)
                    ? false
                    : false,
                propagate: savedField?.propagate || false,
                isForeignkeyField: true,
                tableId: fk.parentTableSid,
                fkLabel: fk.fkName,
                fkMapId: fk.fkMapId,
                isSelfReferred: fk.mappings.find((mapping) => mapping?.child === field.sid) ? true : false,
                mappedField: fk.mappings.some((mapping) => mapping.parent === field.sid),
                fkAliasName: savedField?.foreignKeyAliasName ? savedField?.foreignKeyAliasName : aliasName,
                backupfkAlias: savedField?.foreignKeyAliasName ? savedField?.foreignKeyAliasName : aliasName,
                sid: savedField?.sid ? savedField?.sid : `${fk.fkMapId}_$_${field.sid}`
              };

              return {
                data: formattedFkData,
                children: []
              };
            })
            .filter((node) => node !== null) || [],
        expanded: true
      };
      this.foreignkeyMappings.push(node);
    });

    this.foreignkeyMappings = [...this.foreignkeyMappings];
    this.filteredforeignkeyMappingsTreeNodes = [...this.foreignkeyMappings];
  }

  onCheckboxChange(e, rowData) {
    this.checkBoxChanged = true;
    const originalPartialSearch = rowData.partialSearch;
    const originalPropagate = rowData.propagate;
    let fieldSidForDelete: string = rowData.isForeignkeyField ? rowData.sid : rowData.id;
    if (!rowData.searchable) {
      rowData.partialSearch = false;
      if (rowData.propagate) {
        rowData.propagate = false;
      }
    }
    if (!rowData.searchable) {
      this.loader.show();
      // Build API constants with property and optional foreignKeySid
      // For foreign key fields, use sid (searchableIndexFieldId), for regular fields use id
      const modelIdForDelete = rowData.isForeignkeyField ? rowData.sid : rowData.id;
      this.referenceUtil
        .handleWithReferences({
          payload: {
            modelId: modelIdForDelete,
            modelType: ModelType.SEARCHABLE_INDEX_FIELD,
            actionType: ActionType.SEARCHABLE_DELETE,
            action: UiActions.DELETE
          },
          node: { name: this.formData.name, type: 'Field' },
          onConfirm: () => {
            this.trackFieldChange(rowData);
          },
          onNoReferences: () => {
            this.trackFieldChange(rowData);
          },
          onCancel: () => {
            rowData.searchable = true;
            rowData.partialSearch = originalPartialSearch;
            if (rowData.propagate) {
              rowData.propagate = originalPropagate;
            }
          },
          onError: () => {
            // On error, proceed without blocking but keep UI consistent
            this.trackFieldChange(rowData);
          },
          filter: (refs: any[]) => {
            if (refs?.find((r: any) => r?.manualChange)) {
              this.disableSave = true;
            }
            return refs;
          }
        })
        .catch(() => {
          // Fall back to proceeding with change tracking
          this.trackFieldChange(rowData);
        })
        .finally(() => this.loader.hide());
    } else {
      this.trackFieldChange(rowData);
    }
  }

  /**
   * Tracks changes made to searchable field properties and manages the change tracker state.
   * This method determines whether a field has been added, updated, or deleted based on its
   * current state versus its original saved state, and updates the appropriate change tracker arrays.
   *
   * @param rowData - The field data object containing current field properties
   * @param rowData.searchable - Current searchable status of the field
   * @param rowData.id - Unique identifier of the field
   * @param rowData.isForeignkeyField - Boolean indicating if this is a foreign key field
   * @param rowData.partialSearch - Current partial search setting
   * @param rowData.propagate - Current propagate setting (for foreign key fields)
   * @param rowData.fkAliasName - Current alias name (for foreign key fields)
   *
   */
  trackFieldChange(rowData: any) {
    const isCurrentlySearchable = rowData.searchable;
    let fieldSidForDelete: string;
    if (rowData.isForeignkeyField) {
      fieldSidForDelete = rowData?.sid;
    } else {
      fieldSidForDelete = rowData?.id;
    }

    // Check if field was originally searchable
    const wasOriginallySearchable = this.isFieldOriginallySearchable(fieldSidForDelete, rowData.isForeignkeyField);

    if (isCurrentlySearchable && !wasOriginallySearchable) {
      // Field is being added
      this.addToChangeTracker('added', rowData);
      this.removeFromChangeTracker('deleted', fieldSidForDelete);
      this.removeFromChangeTracker('updated', fieldSidForDelete);
    } else if (!isCurrentlySearchable && wasOriginallySearchable) {
      // Field is being deleted
      this.addToChangeTracker('deleted', fieldSidForDelete);
      this.removeFromChangeTracker('added', fieldSidForDelete);
      this.removeFromChangeTracker('updated', fieldSidForDelete);
    } else {
      // Field might be updated (check for other property changes)
      if (this.hasFieldPropertiesChanged(rowData)) {
        this.addToChangeTracker('updated', rowData);
      } else {
        this.removeFromChangeTracker('updated', fieldSidForDelete);
      }
      this.removeFromChangeTracker('added', fieldSidForDelete);
      this.removeFromChangeTracker('deleted', fieldSidForDelete);
    }
  }

  // Helper method to check if field was originally searchable
  isFieldOriginallySearchable(sid: string, isForeignkeyField: boolean): boolean {
    if (isForeignkeyField) {
      // Check in foreignKeyFields
      for (const fkField of this.savedData?.foreignKeyFields || []) {
        if (fkField.sid == sid) {
          return true;
        }
      }
      return false;
    } else {
      for (const field of this.savedData?.fields || []) {
        if (field.sid == sid) {
          return true;
        }
      }
      return false;
    }
  }

  hasFieldPropertiesChanged(rowData: any): boolean {
    if (rowData.isForeignkeyField) {
      // Find the original foreign key field data
      const originalField = this.savedData?.foreignKeyFields?.find((field) => field.sid === rowData.sid);
      if (originalField) {
        return (
          originalField.partialSearch !== rowData.partialSearch ||
          originalField.propagate !== rowData.propagate ||
          originalField.foreignKeyAliasName !== rowData.fkAliasName ||
          rowData.searchable === false
        );
      }
    } else {
      // Find the original field data
      const originalField = this.savedData?.fields?.find((field) => field.fieldSid === rowData.id);
      if (originalField) {
        return originalField.partialSearch !== rowData.partialSearch || rowData.searchable === false;
      }
    }
    return false;
  }

  addToChangeTracker(type: 'added' | 'updated' | 'deleted', data: any) {
    const targetArray = this.changeTracker[type];
    const fieldId = typeof data === 'string' ? data : data.isForeignkeyField ? data.sid : data.id;

    if (type === 'deleted') {
      if (!targetArray.includes(fieldId)) {
        targetArray.push(fieldId);
      }
    } else {
      const existingIndex = targetArray.findIndex((item: any) => item.id === fieldId || item?.sid === fieldId);
      if (existingIndex >= 0) {
        targetArray[existingIndex] = data; // Update existing
      } else {
        targetArray.push(data); // Add new
      }
    }
  }

  removeFromChangeTracker(type: 'added' | 'updated' | 'deleted', fieldId: string) {
    const targetArray = this.changeTracker[type];
    if (type === 'deleted') {
      const index = targetArray.indexOf(fieldId);
      if (index >= 0) {
        targetArray.splice(index, 1);
      }
    } else {
      const index = targetArray.findIndex((item: any) => item.id === fieldId || item?.sid === fieldId);
      if (index >= 0) {
        targetArray.splice(index, 1);
      }
    }
  }

  generateNewPayload() {
    const payload = {
      addedFields: this.formatFieldsForPayload(this.changeTracker.added),
      updatedFields: this.formatFieldsForPayload(this.changeTracker.updated),
      deletedFieldIds: this.changeTracker.deleted
    };

    return payload;
  }

  formatFieldsForPayload(fields: any[]): any[] {
    return fields.map((field) => {
      const isReferredField = field.isForeignkeyField || false;
      const fkMapId = isReferredField ? field.fkMapId : null;

      return {
        sid: isReferredField ? field.sid : field.id,
        fieldSid: field.id,
        propagate: field.propagate || false,
        partialSearch: field.partialSearch || false,
        foreignKeySid: isReferredField ? fkMapId : null,
        foreignKeyAliasName: isReferredField ? field.fkAliasName || null : null,
        foreignkeyTableId: isReferredField ? field.tableId || null : null,
        foreignkeyIndexId: isReferredField ? this.getForeignKeyIndexId(field) : null,
        foreignkeyRefAppId: isReferredField ? this.getForeignKeyRefAppId(field) : null,
        foreignkeyIndexLastField: isReferredField ? this.getForeignKeyIndexLastField(field) : null,
        type: isReferredField ? 'FOREIGN_KEY_FIELD' : 'FIELD',
        appId: this.appGlobalService.get('appId'),
        tableId: this.formData.sid,
        orgId: this.appGlobalService.get('orgId')
      };
    });
  }

  getForeignKeyIndexId(field: any): string | null {
    const parentNode = this.foreignkeyMappings.find((fk) =>
      fk.children.some((child) => child?.data?.fkMapId === field.fkMapId)
    );
    return parentNode?.data?.fkIndexId || null;
  }

  getForeignKeyRefAppId(field: any): string | null {
    // This might be stored in the foreign key mappings data
    const parentNode = this.foreignkeyMappings.find((fk) =>
      fk.children.some((child) => child?.data?.fkMapId === field?.fkMapId)
    );
    return parentNode?.data?.fkRefAppId || this.appGlobalService.get('appId');
  }

  getForeignKeyIndexLastField(field: any): string | null {
    const parentNode = this.foreignkeyMappings.find((fk) =>
      fk.children.some((child) => child?.data?.fkMapId === field?.fkMapId)
    );
    return parentNode?.data?.fkLastField || null;
  }

  // Function to get all foreign key mappings for a specific table
  getForeignKeyMappings() {
    const fks = this.foreignKeysFromResponse || [];
    const currentAppId = this.appGlobalService.get('appId');

    return fks
      .filter((fk) => !fk.indexRefAppId || fk.indexRefAppId === currentAppId)
      .map((fk) => ({
        fkName: fk.fkName,
        mappings: fk.mappings, // Each mapping contains child and parent references
        parentTableSid: fk.parentTableSid,
        indexRefAppId: fk.indexRefAppId,
        fkMapId: fk.sid
      }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fsData'] || changes['formData']) {
      // this.getSearchableFieldCount();
    }
  }

  showHeader() {
    return `Searchable Fields - `;
  }

  assignTableOrders() {
    const tree = $.ui.fancytree.getTree('#tree');
    this.tableOrders = this.getNodesByType(tree.rootNode?.children, nodeType.TABLE) || [];
  }

  getFieldsUsingTableId(tblId: string) {
    const tableId = tblId; // Get the tableId from formData
    let fields: any[] = [];
    this.assignTableOrders();
    // Iterate through each table in the dataModelOrders
    this.tableOrders.forEach((table: any) => {
      if (table.key === tableId) {
        fields = this.getNodesByType(table?.children, nodeType.FIELD);
      }
    });
    return fields;
  }

  getRefferedFieldsUsingTableId(tblId: string) {
    const tableId = tblId; // Get the tableId from formData
    let fields: any[] = [];
    if (this.referedTableFieldsMap?.[tableId]) {
      this.referedTableFieldsMap?.[tableId]?.forEach((field: any) => {
        fields.push(field);
      });
    } else {
      this.assignTableOrders();
      // Iterate through each table in the dataModelOrders
      this.tableOrders.forEach((table: any) => {
        if (table.key === tableId) {
          fields = this.getNodesByType(table?.children, nodeType.FIELD);
        }
      });
    }
    return fields;
  }

  getNodesByType(arr, targetType) {
    let result: any[] = [];

    for (const item of arr) {
      if (item.type === targetType) {
        result.push(item);
      } else if (item.type === nodeType.GROUP || item.type === nodeType.FIELDGROUP) {
        const children = item?.children;
        if (children && Array.isArray(children)) {
          result = result.concat(this.getNodesByType(children, targetType));
        }
      }
    }

    return result;
  }

  private findSavedForeignKeyField(fkMapId: string, fieldSid: string): any {
    if (!this.savedData?.foreignKeyFields) {
      return {};
    }

    const combinedKey = `${fkMapId}_$_${fieldSid}`;
    for (const key in this.savedData.foreignKeyFields) {
      const fkData = this.savedData.foreignKeyFields[key];

      // Check if this foreign key data matches our criteria
      if (fkData.foreignKeySid === fkMapId && fkData.fieldSid === fieldSid) {
        return fkData;
      }
    }

    return {};
  }

  getSavedData() {
    let fieldsData: any = {};
    let foreignKeyFieldsData: any = {};
    const processData = () => {
      // Merge the data to match the expected structure
      this.savedData = {
        fields: fieldsData || {},
        foreignKeyFields: foreignKeyFieldsData || {}
      };
    };

    // Use forkJoin to call both APIs simultaneously
    forkJoin({
      fieldsData: this.bs.get(ApiConstants.getSearchableIndexFields, { tableId: this.formData.sid }),
      foreignKeyFieldsData: this.bs.get(ApiConstants.getSearchableIndexForeignKeyFields, { tableId: this.formData.sid })
    }).subscribe({
      next: ({ fieldsData: fields, foreignKeyFieldsData: foreignKeyFields }) => {
        fieldsData = fields;
        foreignKeyFieldsData = foreignKeyFields;
        processData();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.translate.instant('SEARCHABLE_TABLE_FIELDS_LOAD_FAILED')
        });
        processData();
      }
    });
  }

  disableCheckBox(rowData, property) {
    if (property === GlobalConstants.searchable) {
      return !!(this.disabledFields.includes(rowData.name) || rowData.isTransient || rowData.isInActive || rowData.isSelfReferred);
    }

    return !!(rowData.isTransient || !rowData.searchable || rowData.isInActive || rowData.isSelfReferred);
  }

  transformToTree(jsonArray) {
    const tree: any = [];

    jsonArray.forEach((item) => {
      // Retrieve saved data for the current item
      const field = item.data || {};
      const savedField = this.savedData?.fields?.find((f) => f.fieldSid === field.sid);
      const isSystemGeneratedField = this.disabledFields.includes(field.name);

      const node = {
        data: {
          name: field.name,
          id: field.sid,
          searchable: isSystemGeneratedField || !!savedField, // Keep sys-generated fields preselected
          isString: this.checkFieldType(field),
          isInActive: field?.name === 'IsActive' && field.sysGen,
          isTransient: field?.f_transientField,
          isSysGen: field?.sysGen,
          partialSearch: savedField?.partialSearch ?? false
        }
      };

      // Push node into tree's children if name and sid are present
      if (field.name && field.sid) {
        if (field?.f_transientField) {
          node.data.name = `${node.data.name} (Transient Field)`;
        }
        tree.push(node);
      }
    });

    return tree;
  }

  // Method to map foreign key fields to the tree
  mapForeignKeyToTree(fkData, foreignKeyFields, foreignKeys) {
    const { mappings } = fkData;
    let lastParentNode: any = null; // Track the last matching parent node
    let mappingLabel = '';

    mappings.forEach((mapping) => {
      const childFieldId = mapping.child;
      const parentFieldId = mapping.parent;

      // Find the parent node in the tree using the childFieldId
      const parentNode = this.findNodeById(this.allFields, childFieldId);

      // Set the current node as the last parent node if it exists
      if (parentNode) {
        lastParentNode = parentNode;
        // Generate the label for the mapping (for updating at the end)
        if (fkData.parentTableSid && parentFieldId) {
          mappingLabel = `${parentNode.data.name} (${this.tableIdMap[fkData.parentTableSid]}.${this.fieldIdMap[parentFieldId]}) `;
        }
        if (mappingLabel) {
          parentNode.data.name = mappingLabel;
        }
        parentNode.data.fkLabel = fkData.fkName;
      }
    });

    if (lastParentNode) {
      const savedForeignKeyFields = this.savedData[lastParentNode.data.id]?.foreignKeyFields?.fields || [];

      // Filter out fields that match any composite key mappings
      // const filteredForeignKeyFields = foreignKeyFields.filter(item => {
      //   const field = item.data || {};
      //   return !mappings.some(m => m.parent === field.sid);
      // });

      const hasMappings = foreignKeyFields.some((field) => {
        return mappings.some((mapping) => mapping.parent === field.data.sid);
      });

      // Add filtered foreign key fields to the last parent node
      foreignKeyFields.forEach((item) => {
        const field = item.data || {};
        const matchingField = savedForeignKeyFields.find((fkField) => fkField?.fieldId === field.sid);

        if (field.sid !== lastParentNode.data.id) {
          // Exclude mapped fields
          lastParentNode.children.push({
            data: {
              name: field.name,
              id: field.sid,
              isForeignkeyField: true,
              isTransient: field?.f_transientField,
              isInActive: false,
              isString: this.checkFieldType(field),
              propagate: matchingField?.propagate || false,
              searchable: !!matchingField,
              partialSearch: matchingField?.partialSearch ?? false,
              tableId: field.f_tableId
            },
            children: []
          });
        }
      });
    }

    const groupedFields: any = [];
    const nonGroupedFields: any[] = [];

    this.allFields.forEach((field) => {
      const fkLabel = field.data.fkLabel;
      if (fkLabel) {
        if (!groupedFields[fkLabel]) {
          groupedFields[fkLabel] = [];
        }
        groupedFields[fkLabel].push(field);
      } else {
        nonGroupedFields.push(field); // Add non-grouped fields separately
      }
    });

    // Sort fields within each group based on their position in the mappings array
    Object.keys(groupedFields).forEach((fkLabel) => {
      const group = groupedFields[fkLabel];

      // Sort the fields within this group according to the order in the mappings array
      group.sort((a, b) => {
        const aIndex = mappings.findIndex((mapping) => mapping.child === a.data.id);
        const bIndex = mappings.findIndex((mapping) => mapping.child === b.data.id);
        return aIndex - bIndex; // Sort by the order in the mapping
      });
    });

    const sortedGroupedFields: any = [];
    foreignKeys.forEach((fk) => {
      const fkName = fk.fkName;
      if (groupedFields[fkName]) {
        sortedGroupedFields.push(...groupedFields[fkName]); // Add the group in the order of fkName
      }
    });

    // Flatten the grouped fields and assign back to allFields
    const sortedFields = Object.values(sortedGroupedFields).flat();
    this.allFields = [...nonGroupedFields, ...sortedFields];
    let currentFkLabel = null;
    let fieldsInFkLabelGroup: any = [];

    this.allFields.forEach((field, index) => {
      const fkLabel = field?.data.fkLabel;

      if (fkLabel) {
        if (fkLabel !== currentFkLabel) {
          if (fieldsInFkLabelGroup.length > 0) {
            fieldsInFkLabelGroup[0].data.isFirst = true; // First field in the group
            fieldsInFkLabelGroup[fieldsInFkLabelGroup.length - 1].data.isLast = true; // Last field in the group
          }

          // Reset for the new fkLabel group
          fieldsInFkLabelGroup = [field];
          currentFkLabel = fkLabel;
        } else {
          fieldsInFkLabelGroup.push(field);
        }

        if (index === this.allFields.length - 1) {
          fieldsInFkLabelGroup[fieldsInFkLabelGroup.length - 1].data.isLast = true;
        }
      }
    });

    // Apply `isFirst` and `isLast` to the last group if it has `fkLabel`
    if (fieldsInFkLabelGroup.length > 0) {
      fieldsInFkLabelGroup[0].data.isFirst = true;
      fieldsInFkLabelGroup[fieldsInFkLabelGroup.length - 1].data.isLast = true;
    }
  }

  findNodeById(tree, id) {
    for (const node of tree) {
      if (node.data.id === id) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const foundNode = this.findNodeById(node.children, id);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  }

  updateFields() {
    if (this.formHasError) {
      return;
    }
    this.checkBoxChanged = false;
    this.closeAddFieldsModalDialogue();
    const reqPayLoad = this.generateNewPayload();

    const saveapiConstants = ApiConstants.searchableIndexCudOperations;
    const updateUrl = `${saveapiConstants.url}`;
    this.bs.post({ url: updateUrl }, reqPayLoad).subscribe({
      next: (response: any) => {
        /* empty */
      },
      error: () => console.info('error occurred')
    });
  }

  validateBeforeSave(event: Event, data: any) {
    if (!this.onNameChange(event, data)) {
      this.formHasError = true;
      setTimeout(() => {
        this.formHasError = false;
      }, 1000); // Adjust the timeout duration as needed
    } else {
      data.backupfkAlias = data.fkAliasName;
      this.formHasError = false;
      this.checkBoxChanged = true;
      this.trackFieldChange(data);
    }
  }

  onNameChange(event: Event, data: any) {
    const myRe = /^[a-zA-Z][a-zA-Z0-9_]*$/;
    const aliasName = data?.fkAliasName?.trim();
    const originalAlias = data?.backupfkAlias?.trim();
    if (aliasName) {
      data.fkAliasName = aliasName;
    }
    const array = myRe.exec(aliasName);

    // Check if alias name is empty
    if (!aliasName) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translate.instant('ALIAS_NAME_REQUIRED_COMPOSIE_FK')
      });
      data.fkAliasName = originalAlias;
      return false;
    }

    if (!array) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translate.instant('ALPHANUMEREIC_CHARACTERS_ALLOWED_ALIAS_NAME')
      });
      data.fkAliasName = originalAlias;
      return false;
    }

    // Check if alias name exceeds length limit
    if (aliasName.length > this.util.titleMaxLength) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translate.instant('NAME_MAX_LENGTH_ERROR', { maxNo: this.util.titleMaxLength })
      });
      data.fkAliasName = originalAlias;
      return false;
    }

    // Check for duplicates
    const isDuplicate = this.foreignkeyMappings.some((fk: any) =>
      fk.children.some(
        (child: any) => child.data.fkAliasName?.toLowerCase() === aliasName?.toLowerCase() && child.data.id !== data.id
      )
    );
    if (isDuplicate) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translate.instant('DUPLICATE_ALIAS_NAME_NOT_ALLOWED')
      });
      data.fkAliasName = originalAlias;
      return false;
    }
    // Check if alias name is a field name of the current or parent table
    const tblfields = [...this.getRefferedFieldsUsingTableId(this.formData.sid)];
    const currentTableFields = tblfields.map((field) => field.data?.name.toLowerCase().replace(/\s+/g, ''));
    if (currentTableFields.includes(aliasName.toLowerCase().replace(/\s+/g, ''))) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translate.instant('ALIAS_NAME_CANNOT_BE_FIELD_NAME_OF_CURRENT_TABLE')
      });
      data.fkAliasName = originalAlias;
      return false;
    }
    // Validation passed - keep the new alias name
    data.fkAliasName = aliasName;
    return true;
  }

  closeAddFieldsModalDialogue() {
    if (this.formHasError) {
      return;
    }
    if (this.checkBoxChanged) {
      this.callConfirmationService();
    } else {
      this.showSearchModal = false;
      this.searchValue = '';
      this.loader.hide();
      this.tableFieldsLoaded = false;
      this.foreignKeyFieldsLoaded = false;
    }
  }

  callConfirmationService() {
    this.confirmationDialogService.confirm({
      message: this.translate.instant('UN_SAVED_CUD'),
      accept: () => {
        this.showSearchModal = false;
        this.searchValue = '';
        this.loader.hide();
      },
      reject: () => {
        /* empty */
      }
    });
  }

  checkFieldType(field) {
    return (
      ['string', 'dropdown', 'text', 'radio', 'email', 'url'].includes(field.f_type?.toLowerCase()) &&
      !this.partialSearchRestricted?.includes(field.name)
    );
  }

  ngOnInit(): void {
    this.getSavedData();
    this.resetChangeTracker();
    this.disableSave = false;
  }

  resetChangeTracker() {
    this.changeTracker = {
      added: [],
      updated: [],
      deleted: []
    };
  }

  onNodeExpand(event) {
    const rowData = event.node.data;
    this.handleNodeStateChange(rowData, true); // Expand the relevant node
  }

  onNodeCollapse(event) {
    const rowData = event.node.data;
    this.handleNodeStateChange(rowData, false);
  }

  handleNodeStateChange(rowData, expanded: boolean) {
    const lastNode = this.findLastNodeByFkLabel(rowData.fkLabel);

    if (rowData.isFirst) {
      if (lastNode && lastNode.expanded !== expanded) {
        lastNode.expanded = expanded;
      }
    }

    if (rowData.isLast) {
      if (!expanded) {
        console.log(`Last node collapsed: ${rowData.name}`);
      }
    }
  }

  findLastNodeByFkLabel(fkLabel: string) {
    if (this.allFields.length > 0 && fkLabel) {
      const filteredFields = this.allFields.filter((field) => field.data.fkLabel === fkLabel && field.data.isLast);
      if (filteredFields.length > 0) {
        // const sortedFields = filteredFields.slice().sort((a, b) => {
        //   return a.data.fkLabel.localeCompare(b.data.fkLabel);
        // });

        // Return the last node in the sorted filtered list
        return filteredFields[0];
      }
    }
    return null;
  }

  generateFKAliasName(fkName: string, parentTableField: string): string {
    let alias = `${fkName}${parentTableField?.charAt(0)?.toUpperCase() + parentTableField?.slice(1)}`;
    let counter = 2;

    // Ensure alias is unique by checking the set
    while (this.generatedAliases.has(alias)) {
      alias = `${fkName}${counter}${parentTableField?.charAt(0)?.toUpperCase() + parentTableField?.slice(1)}`;
      counter++;
    }

    // Store the new alias
    this.generatedAliases.add(alias);

    return alias;
  }

  // Search - Referenced Fields

  filterTree() {
    if (!this.foreignKeySearch) {
      this.filteredforeignkeyMappingsTreeNodes = [...this.foreignkeyMappings]; // Reset filter
      return;
    }

    this.filteredforeignkeyMappingsTreeNodes = this.filterTreeNodes(
      this.foreignkeyMappings,
      this.foreignKeySearch.toLowerCase()
    );
  }

  filterTreeNodes(nodes: any[], searchText: string): any[] {
    return nodes
      .map((node) => {
        const match =
          node.data?.name?.toLowerCase().includes(searchText) ||
          node.data?.fkAliasName?.toLowerCase().includes(searchText);
        const filteredChildren = node?.children ? this.filterTreeNodes(node.children, searchText) : [];

        if (match || filteredChildren?.length) {
          return { ...node, children: filteredChildren, expanded: true };
        }
        return null;
      })
      .filter((node) => node !== null);
  }

  // on Tab switch rest the data
  resetData() {
    this.foreignKeySearch = '';
    this.filterTree();
    this.clearSearchData();
  }

  clearSearchData() {
    // Table fields - to clear the search value
    const searchInput = document.querySelector('.search-input-text') as HTMLInputElement;
    this.searchValue = '';
    if (searchInput) {
      searchInput.value = '';
      this.searchTbl.filterGlobal('', 'contains');
    }
  }

  tooltipTemplate = `<div class="custom-tooltip-content" style="line-height: 1.2;">
    <p style="margin: 0; padding: 0;">Enabling this option ensures that the unmapped foreign key fields in the index are kept up-to-date. This option applies only to foreign key table fields that are not associated with any foreign key mapping.</p>
    <p style="margin: 0; padding: 0;"><strong>Example:</strong></p>
    <p style="margin: 0; padding: 0;">You have two tables: Employee and Country.</p>
    <table border="1" style="border-collapse: collapse; width: 100%; text-align: left;">
        <thead>
            <tr>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Country (FK)</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td>1</td>
                <td>John Doe</td>
                <td>101</td>
            </tr>
            <tr>
                <td>2</td>
                <td>Jane Smith</td>
                <td>102</td>
            </tr>
        </tbody>
    </table>
    <table border="1" style="border-collapse: collapse; width: 100%; text-align: left;">
        <thead>
            <tr>
                <th>Country ID</th>
                <th>Code</th>
                <th>Name</th>
                <th>Short Name</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td>101</td>
                <td>US</td>
                <td>United States</td>
                <td>USA</td>
            </tr>
            <tr>
                <td>102</td>
                <td>CA</td>
                <td>Canada</td>
                <td>CA</td>
            </tr>
        </tbody>
    </table>
    <p style="margin: 0; padding: 0;">The 'Code' field from the Country Table is mapped to the Employee Table's 'Country' field and can be marked as 'Searchable' to enable search functionality.</p>
    <p style="margin: 0; padding: 0;">To search for Employees using the 'Name' and 'Short Name' fields from the Country Table, you can mark these fields as 'Searchable.'</p>
    <p style="margin: 0; padding: 0;">Now, when the 'Propagate' option is enabled for the 'Name' field (e.g., "United States"), any changes made to the 'Name' field in the Country Table will automatically update the Employee index in Elasticsearch.</p>
    <p style="margin: 0; padding: 0;"><strong>For example:</strong> If the 'Name' field in the Country Table changes from "United States" to "USA": The Employee index in Elasticsearch will automatically reflect this update. This ensures that search results in the Employee Table will always show the most up-to-date Country Name.</p>
    <p style="margin: 0; padding: 0;"><strong>Note:</strong> The 'Propagate' option is only applicable for foreign key table fields that are not linked to a foreign key mapping. This ensures that changes to the unmapped fields are automatically reflected in the search index.</p>
</div>`;
}



